import { Link } from 'react-router-dom';
import type { Article } from '@/mocks/articles';

interface ArticleCardProps {
  article: Article;
  variant?: 'default' | 'large' | 'compact';
}

export default function ArticleCard({ article, variant = 'default' }: ArticleCardProps) {
  const handleClick = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  if (variant === 'large') {
    return (
      <Link
        to={`/article/${article.slug}`}
        onClick={handleClick}
        className="group block bg-background-50 rounded-lg border border-background-200/70 overflow-hidden cursor-pointer transition-colors hover:border-primary-300/50"
      >
        <div className="relative w-full h-64 overflow-hidden">
          <img
            src={article.image}
            alt={article.title}
            className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
          />
          <div className="absolute top-3 left-3">
            <span className="px-3 py-1 rounded-full text-xs font-medium bg-primary-500 text-background-50">
              {article.category}
            </span>
          </div>
        </div>
        <div className="p-5">
          <h3 className="font-heading text-lg font-semibold text-foreground-950 group-hover:text-primary-600 transition-colors line-clamp-2 mb-2">
            {article.title}
          </h3>
          <p className="text-sm text-foreground-600 line-clamp-2 mb-4 leading-relaxed">
            {article.excerpt}
          </p>
          <div className="flex items-center justify-between text-xs text-foreground-500">
            <div className="flex items-center gap-3">
              <span className="flex items-center gap-1">
                <i className="ri-user-line"></i>
                {article.author}
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-calendar-line"></i>
                {article.date}
              </span>
            </div>
            <span className="flex items-center gap-1">
              <i className="ri-eye-line"></i>
              {article.views >= 10000 ? `${(article.views / 10000).toFixed(1)}万` : article.views}
            </span>
          </div>
        </div>
      </Link>
    );
  }

  if (variant === 'compact') {
    return (
      <Link
        to={`/article/${article.slug}`}
        onClick={handleClick}
        className="group flex gap-4 p-3 rounded-lg hover:bg-background-100 transition-colors cursor-pointer"
      >
        <div className="relative w-24 h-24 flex-shrink-0 rounded-lg overflow-hidden">
          <img
            src={article.image}
            alt={article.title}
            className="w-full h-full object-cover object-top"
          />
        </div>
        <div className="flex-1 min-w-0">
          <h4 className="font-heading text-sm font-semibold text-foreground-950 group-hover:text-primary-600 transition-colors line-clamp-2 mb-1">
            {article.title}
          </h4>
          <p className="text-xs text-foreground-600 line-clamp-1 mb-2">{article.excerpt}</p>
          <div className="flex items-center gap-2 text-xs text-foreground-500">
            <span>{article.date}</span>
            <span>·</span>
            <span>{article.readTime} 分钟阅读</span>
          </div>
        </div>
      </Link>
    );
  }

  return (
    <Link
      to={`/article/${article.slug}`}
      onClick={handleClick}
      className="group block bg-background-50 rounded-lg border border-background-200/70 overflow-hidden cursor-pointer transition-colors hover:border-primary-300/50"
    >
      <div className="relative w-full h-48 overflow-hidden">
        <img
          src={article.image}
          alt={article.title}
          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
        />
        <div className="absolute top-3 left-3">
          <span className="px-2.5 py-0.5 rounded-full text-xs font-medium bg-accent-500 text-background-50">
            {article.category}
          </span>
        </div>
      </div>
      <div className="p-4">
        <h3 className="font-heading text-base font-semibold text-foreground-950 group-hover:text-primary-600 transition-colors line-clamp-2 mb-2">
          {article.title}
        </h3>
        <p className="text-sm text-foreground-600 line-clamp-2 mb-3 leading-relaxed">
          {article.excerpt}
        </p>
        <div className="flex items-center justify-between text-xs text-foreground-500">
          <div className="flex items-center gap-2">
            <span>{article.author}</span>
            <span>·</span>
            <span>{article.readTime} 分钟阅读</span>
          </div>
          <span>{article.date}</span>
        </div>
      </div>
    </Link>
  );
}